import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Collapse, Offcanvas } from 'react-bootstrap';
import { useAuth } from '../auth';
import { roleLabel } from './roleLabels';

// homepage/user_experience.md #4 (revised) - the menu button lives IN the top bar (a standard
// hamburger toggler), not as a floating button. Below lg it opens the full navigation as a left
// off-canvas; the desktop horizontal bar in PrimaryNav covers wider screens. Identical in every
// vertical; the menu contents come from ../navConfig.js via the `nav` prop. (The "RentalVerse
// home" link is in the top bar itself, not in here.)
export default function MobileNavMenu({ nav, isAuthenticated }) {
  const [open, setOpen] = useState(false);
  // "All listings" / Browse is a collapsible submenu inside the menu (collapsed by default).
  const [browseOpen, setBrowseOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const close = () => setOpen(false);

  const roles = user?.roles?.length ? user.roles : [user?.role].filter(Boolean);

  const handleLogout = () => {
    close();
    logout();
    navigate('/');
  };

  return (
    <>
      <button
        type="button"
        className="navbar-toggler d-lg-none rv-nav-menu-btn"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
      >
        <span className="navbar-toggler-icon" />
      </button>

      <Offcanvas
        show={open}
        onHide={close}
        placement="start"
        className="d-lg-none rv-mobile-nav"
        aria-labelledby="mobile-nav-title"
      >
        <Offcanvas.Header closeButton className="py-2">
          <Offcanvas.Title id="mobile-nav-title" className="fs-6">{nav.brand}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          {isAuthenticated && (
            <div className="border-bottom pb-2 mb-1">
              <div className="fw-semibold text-truncate">{user.name}</div>
              <div className="text-muted small text-truncate">{user.email}</div>
              <div className="mt-1 d-flex flex-wrap gap-1">
                {roles.map((r) => <span key={r} className="badge bg-secondary">{roleLabel(r)}</span>)}
              </div>
            </div>
          )}

          {nav.browseCategories?.length > 0 && (
            <div>
              <button
                type="button"
                className="rv-mobile-nav-link rv-mobile-nav-toggle d-flex justify-content-between align-items-center w-100 btn btn-link text-decoration-none text-start"
                onClick={() => setBrowseOpen((v) => !v)}
                aria-expanded={browseOpen}
                aria-controls="mobile-browse-submenu"
              >
                <span>Browse listings</span>
                <span aria-hidden="true">{browseOpen ? '▾' : '▸'}</span>
              </button>
              <Collapse in={browseOpen}>
                <div id="mobile-browse-submenu">
                  <Link className="rv-mobile-nav-link ps-3" to="/" onClick={close}>All listings</Link>
                  {nav.browseCategories.map((c) => (
                    <Link
                      key={c.value}
                      className="rv-mobile-nav-link ps-3"
                      to={`/?category=${encodeURIComponent(c.value)}`}
                      onClick={close}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </Collapse>
            </div>
          )}

          {(nav.links || []).length > 0 && <div className="text-uppercase small text-muted mt-1">Menu</div>}
          {(nav.links || []).map((item, i) => {
            if (item.type === 'group') {
              return (
                <div key={`g${i}`}>
                  <div className="small fw-semibold mt-1">{item.label}</div>
                  {item.items.map((s) => (
                    <Link key={s.to} className="rv-mobile-nav-link ps-3" to={s.to} onClick={close}>{s.label}</Link>
                  ))}
                </div>
              );
            }
            if (item.external) {
              return <a key={`l${i}`} className="rv-mobile-nav-link" href={item.to} onClick={close}>{item.label}</a>;
            }
            return (
              <Link key={`l${i}`} className="rv-mobile-nav-link" to={item.to} onClick={close}>
                {item.live && <span aria-hidden="true">👁 </span>}{item.label}
              </Link>
            );
          })}

          {!isAuthenticated && (nav.loggedOutLinks || []).map((l) => (
            <Link key={l.to} className="rv-mobile-nav-link" to={l.to} onClick={close}>{l.label}</Link>
          ))}

          <div className="mt-2 pt-2 border-top d-flex flex-column">
            {isAuthenticated ? (
              <>
                <Link className="rv-mobile-nav-link" to="/account" onClick={close}>Account details</Link>
                <Link className="rv-mobile-nav-link" to="/pricing" onClick={close}>Pricing &amp; plans</Link>
                <Link className="rv-mobile-nav-link" to="/account/password" onClick={close}>Change password</Link>
                <button type="button" className="rv-mobile-nav-link text-start btn btn-link p-0" onClick={handleLogout}>Log out</button>
              </>
            ) : (
              <>
                <Link className="rv-mobile-nav-link" to="/pricing" onClick={close}>Pricing</Link>
                <div className="d-flex gap-2 mt-1">
                  <Link className="btn btn-primary flex-fill" to="/login" onClick={close}>Log in</Link>
                  <Link className="btn btn-outline-primary flex-fill" to="/signup" onClick={close}>Sign up</Link>
                </div>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
